import React from 'react'
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material'
import { AutoCoderFramework, AutoCoderCode } from '../../types/gotypes'
import { MATCH_EVIDENCE_COLORS, resolveColor } from './colorPalette'

// Helper component to render a code chip with optional description tooltip
const CodeChip: React.FC<{
  code: AutoCoderCode
  backgroundColor?: string
  variant?: 'filled' | 'outlined'
  size?: 'small' | 'medium'
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
}> = ({ code, backgroundColor, variant = 'filled', size = 'medium', color }) => {
  const chip = (
    <Chip
      label={code.text || code.code}
      variant={variant}
      size={size}
      color={color}
      sx={
        backgroundColor
          ? {
              backgroundColor,
              color: '#fff',
            }
          : undefined
      }
    />
  )

  if (code.description) {
    return (
      <Tooltip title={code.description} arrow placement="top">
        {chip}
      </Tooltip>
    )
  }

  return chip
}

interface AutoCoderFrameworkOverviewProps {
  frameworks: AutoCoderFramework[]
  colorMap?: Record<string, string>
}

const AutoCoderFrameworkOverview: React.FC<AutoCoderFrameworkOverviewProps> = ({
  frameworks,
  colorMap,
}) => {
  if (!frameworks || frameworks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No frameworks configured</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {frameworks.map((framework, index) => (
        <Paper key={index} elevation={1} sx={{ p: 3, mb: index < frameworks.length - 1 ? 3 : 0 }}>
          <Typography variant="h6" gutterBottom>
            Code Frame: {framework.name}
          </Typography>

          {/* Main Variable */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Main Variable: {framework.main_variable.name}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {framework.main_variable.codes.map((code, codeIndex) => (
                <Box key={codeIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Chip
                    label={code.text || code.code}
                    size="small"
                    sx={{
                      backgroundColor: resolveColor(code.code, colorMap, MATCH_EVIDENCE_COLORS, codeIndex),
                      color: '#fff',
                      minWidth: 80,
                      flexShrink: 0,
                    }}
                  />
                  {code.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 0.25 }}>
                      {code.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Looped Variables */}
          {framework.looped_variables && framework.looped_variables.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Looped Variables
              </Typography>
              {framework.looped_variables.map((loopedVar, varIndex) => (
                <Box key={varIndex} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                    {loopedVar.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {loopedVar.codes.map((code, codeIndex) => (
                      <CodeChip
                        key={codeIndex}
                        code={code}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Suggestions */}
          {framework.suggestions && framework.suggestions.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Existing Suggestions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {framework.suggestions.map((suggestion, suggIndex) => (
                  <CodeChip
                    key={suggIndex}
                    code={suggestion}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  )
}

export default AutoCoderFrameworkOverview
